import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { UnsentChatMessage } from "@/features/data-curation/components/UnsentChatMessage";
import { Check } from "lucide-react";

type ApproveSchemaDialogProps = {
  open: boolean;
  /** Approve and start the collection; else approve the columns only. */
  start: boolean;
  /** The person has unsaved edits; they are saved before the approval. */
  saveFirst: boolean;
  /** Comprehensive mode: the columns are already locked, so this approves the read. */
  fullRead?: boolean;
  busy: boolean;
  agentWorking: boolean;
  /** The message that the person typed in the chat box and did not send, when this starts. */
  unsentMessage: string;
  sendUnsent: boolean;
  onSendUnsentChange: (send: boolean) => void;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
};

const title = (start: boolean, fullRead: boolean) => {
  if (fullRead) return "Approve the full read?";
  return start ? "Approve the columns and start the collection?" : "Approve the columns only?";
};

const description = (start: boolean, fullRead: boolean) => {
  if (fullRead) {
    return "The agent then reads every paper of the master list in the chat. Each paper is one reader call, and the read can take hours. You can still add a column later.";
  }
  return start
    ? "The agent then starts the collection in the chat. You can still add, edit, or delete a column later."
    : "The agent does not start the collection. The chat stays open, so you can keep talking with the agent about the scope of the run. When you are ready, click Start collection. You can still add, edit, or delete a column later.";
};

const confirmLabel = (start: boolean, fullRead: boolean, saveFirst: boolean) => {
  if (fullRead) return saveFirst ? "Save and approve" : "Approve the full read";
  if (start) return saveFirst ? "Save, approve, and start" : "Approve and start";
  return saveFirst ? "Save and approve only" : "Approve columns only";
};

export const ApproveSchemaDialog = ({
  open,
  start,
  saveFirst,
  fullRead = false,
  busy,
  agentWorking,
  unsentMessage,
  sendUnsent,
  onSendUnsentChange,
  error,
  onConfirm,
  onClose,
}: ApproveSchemaDialogProps) => (
  <Dialog
    open={open}
    onOpenChange={(next) => {
      if (!next && !busy) onClose();
    }}
  >
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title(start, fullRead)}</DialogTitle>
        <DialogDescription>
          {saveFirst && "Your edits are saved first. "}
          {description(start, fullRead)}
        </DialogDescription>
      </DialogHeader>
      {start && unsentMessage && (
        <UnsentChatMessage
          text={unsentMessage}
          send={sendUnsent}
          disabled={busy}
          onSendChange={onSendUnsentChange}
        />
      )}
      {agentWorking && (
        <p className="text-sm text-zinc-600" aria-live="polite">
          The agent is working. Wait until it stops, or stop it in the chat.
        </p>
      )}
      {error && (
        <p className="mt-2 rounded-[3px] bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          loading={busy}
          disabled={agentWorking}
          aria-label={busy ? "Approving columns" : undefined}
        >
          <Check className="h-4 w-4" aria-hidden />
          {confirmLabel(start, fullRead, saveFirst)}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
